<script setup lang="ts">
import {computed, ref} from 'vue';
import ChatPresenter from "@/utils/presenters/ChatPresenter.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import NamaLabel from "@/components/NamaLabel.vue";
import Translator from "@/utils/common/Translator.ts";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import ChatAddMemberDialog from "@/components/navBar/chat/ChatAddMemberDialog.vue";
import ChatViewMembersDialog from "@/components/navBar/chat/ChatViewMembersDialog.vue";
import EntityReferenceData = namaTypes.EntityReferenceData;

const props = defineProps<{
  presenter: ChatPresenter;
  isOtherUserOnline: boolean;
}>();

const meta = computed(() => props.presenter.currentChatMetadata.value);
const isGroup = computed(() => meta.value?.chatConversationType === 'Group');
const isAdmin = computed(() => props.presenter.isCurrentUserAdmin());
const participants = computed(() => meta.value?.participants ?? []);
const otherParticipants = computed(() =>
  participants.value.filter(p => p.participant?.id !== props.presenter.currentUserInfo?.id)
);

const editingName = ref(false);
const editNameValue = ref('');
const drawer = ref(false);

function startEditName() {
  editNameValue.value = meta.value?.chatTitle ?? '';
  editingName.value = true;
}

async function saveGroupName() {
  const newName = editNameValue.value.trim();
  if (!newName || newName === meta.value?.chatTitle) {
    editingName.value = false;
    return;
  }
  await props.presenter.updateGroupTitle(newName);
  editingName.value = false;
}

function cancelEditName() {
  editingName.value = false;
}

const showAddMember = ref(false);
const showViewMembers = ref(false);

function onMemberAdded() {
  showAddMember.value = false;
}

async function removeMember(user: EntityReferenceData) {
  await props.presenter.removeParticipant(user);
}

async function makeAdmin(user: EntityReferenceData) {
  await props.presenter.makeParticipantAdmin(user);
}

async function removeAdmin(user: EntityReferenceData) {
  await props.presenter.removeParticipantAdmin(user);
}

const isOwner = computed(() => props.presenter.isCurrentUserGroupOwner());

function getOwnerName(): string {
  const owner = meta.value?.groupOwner;
  if (!owner)
    return '';
  if (owner.id === props.presenter.currentUserInfo?.id)
    return Translator.translateId('You');
  return Translator.translateRef(owner);
}

function isParticipantAdmin(participantId: string | undefined): boolean {
  if (!participantId) return false;
  const p = participants.value.find(p => p.participant?.id === participantId);
  return p?.role === 'Admin' || meta.value?.groupOwner?.id === participantId;
}

function toggleChatInfo() {
  drawer.value = !drawer.value;
}

defineExpose({toggleChatInfo});
</script>

<template>
  <q-drawer
    v-model="drawer"
    side="right"
    :width="350"
    :breakpoint="500"
    bordered
    @keydown.esc="drawer = false"
    class="tw-flex tw-flex-col tw-w-full tw-pb-3 dark:tw-bg-namaBackground-dim-dark tw-bg-namaBackground-dim-light tw-text-namaFontColor-darkGray dark:tw-text-namaFontColor-dark"
  >
    <q-card-section
      class="tw-w-full tw-h-fit tw-flex tw-items-center tw-p-1 tw-shrink-0 dark:tw-bg-namaBackground-dim-dark tw-bg-namaBackground-dim-light">
      <q-toolbar class="tw-h-10">
        <q-btn unelevated no-caps dense @click="drawer = false"
               :title="Translator.translateId('close')"
               class="tw-p-1 tw-rounded-md tw-text-namaFontColor-gray dark:tw-text-namaFontColor-dark tw-w-fit tw-mr-2">
          <NamaIcon default-icon="close" class="tw-size-6 !tw-text-2xl"
                    :class="[Translator.isArabic() ? 'tw-rotate-180' : '']"/>
        </q-btn>
        <q-toolbar-title class="tw-text-primary-light dark:tw-text-primary-dark tw-text-base tw-font-semibold">
          <NamaLabel :text="isGroup ? 'chatGroupInfo' : 'chatUserInfo'"/>
        </q-toolbar-title>
      </q-toolbar>
    </q-card-section>
    <q-card-section class="tw-w-full tw-p-0">
      <div class="tw-flex tw-flex-col tw-items-center tw-py-3 tw-gap-3 tw-w-full">
        <q-avatar size="96px" color="primary">
          <img v-if="!isGroup && meta?.imageSrc" :src="meta.imageSrc" alt="">
          <div v-else-if="isGroup"
               class="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-bg-transparent tw-text-white">
            <NamaIcon defaultIcon="group" class="!tw-text-[40px] active-white"/>
          </div>
          <div v-else
               class="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-bg-gray-300 dark:tw-bg-gray-600 tw-text-white">
            <NamaIcon defaultIcon="person" class="!tw-text-4xl"/>
          </div>
        </q-avatar>
        <div class="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-w-full tw-max-w-[80%]">
          <div v-if="editingName" class="tw-flex tw-flex-row tw-items-center tw-gap-2">
            <input v-model="editNameValue"
                   @keydown.enter="saveGroupName"
                   @keydown.esc="cancelEditName"
                   class="tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-rounded-md tw-px-3 tw-py-1 tw-text-center tw-text-lg tw-font-semibold
                        tw-bg-transparent tw-text-namaFontColor-light dark:tw-text-namaFontColor-dark tw-outline-none
                        focus:tw-border-primary-light dark:focus:tw-border-primary-dark tw-w-full"
                   ref="editNameInput" autofocus/>
            <q-btn flat dense icon="check" color="positive" size="sm" class="tw-rounded-md" @click="saveGroupName"/>
            <q-btn flat dense icon="close" color="negative" size="sm" class="tw-rounded-md" @click="cancelEditName"/>
          </div>
          <div v-else class="tw-flex tw-flex-row tw-w-full tw-justify-center tw-gap-2">
            <span
              class="tw-w-fit tw-max-w-[95%] tw-line-clamp-1 tw-text-center tw-text-lg tw-font-semibold tw-text-namaFontColor-light dark:tw-text-namaFontColor-dark tw-truncate"
              v-text="meta?.chatTitle ?? ''"/>
            <q-btn v-if="isGroup && isAdmin" unelevated no-caps dense size="sm" @click="startEditName"
                   class="tw-p-1 tw-rounded-md tw-text-namaFontColor-gray dark:tw-text-namaFontColor-dark tw-w-fit ">
              <NamaIcon defaultIcon="edit" class="!tw-text-xl"/>
            </q-btn>
          </div>
          <div v-if="!isGroup" class="tw-flex tw-items-center tw-gap-1.5">
            <NamaIcon defaultIcon="circle" class="!tw-text-[8px]"
                      :class="[isOtherUserOnline ? 'active-green' : 'active-gray']"/>
            <NamaLabel :text="isOtherUserOnline ? 'activeNow' : 'notActive'"
                       class="!tw-text-sm !tw-text-gray-500 dark:!tw-text-gray-400"/>
          </div>
          <div v-if="isGroup" class="tw-flex tw-flex-col tw-place-items-center tw-px-4">
            <div class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-500 dark:tw-text-gray-400">
              <NamaIcon defaultIcon="admin_panel_settings" class="tw-size-4"/>
              <span v-text="Translator.translateId('chatGroupOwner') + ': ' +  getOwnerName()"/>
            </div>
            <span class="tw-text-xs tw-font-normal tw-text-gray-500 dark:tw-text-gray-400 tw-uppercase tw-tracking-wide"
                  v-text="participants.length + ' ' + Translator.translateId('chatParticipants')"/>
          </div>
        </div>
      </div>
    </q-card-section>
    <q-card-section v-if="isGroup" class="tw-w-full tw-p-0 tw-grow tw-overflow-y-auto tw-overflow-x-hidden">
      <div class="tw-px-4 tw-pt-4 tw-pb-2 tw-grow tw-overflow-y-auto tw-overflow-x-hidden">
        <div class="tw-flex tw-items-center tw-justify-between tw-mb-2">
          <q-btn unelevated no-caps dense @click="showViewMembers = true"
                 class="tw-p-1 tw-rounded-md tw-text-namaFontColor-gray dark:tw-text-namaFontColor-dark tw-w-fit">
            <NamaIcon defaultIcon="search" class="tw-size-5"/>
            <q-tooltip v-text="Translator.translateId('chatViewMembers')"/>
          </q-btn>
          <span class="tw-text-sm tw-font-semibold tw-text-gray-500 dark:tw-text-gray-400 tw-uppercase tw-tracking-wide"
                v-text="participants.length + ' ' + Translator.translateId('chatParticipants')"/>
        </div>
        <q-list class="tw-rounded-lg">
          <q-item v-if="isAdmin" class="tw-rounded-lg tw-px-0 tw-min-h-[48px]">
            <q-btn unelevated no-caps dense @click="showAddMember = true"
                   class="tw-p-1 tw-rounded-md tw-text-namaFontColor-gray dark:tw-text-namaFontColor-dark tw-w-fit tw-pr-5">
              <q-item-section avatar class="tw-min-w-0 tw-pr-3">
                <q-avatar size="40px" color="primary">
                  <div class="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-bg-transparent tw-text-white">
                    <NamaIcon defaultIcon="add" class="!tw-text-2xl active-white"/>
                  </div>
                </q-avatar>
              </q-item-section>
              <q-item-section>
                <q-item-label class="tw-font-medium tw-text-namaFontColor-light dark:tw-text-namaFontColor-dark">
                  <span v-text="Translator.translateId('chatAddUser')"/>
                </q-item-label>
              </q-item-section>
            </q-btn>
          </q-item>
          <q-item v-for="p in (isGroup ? participants : otherParticipants)" :key="p.participant?.id"
                  class="tw-rounded-lg tw-px-0 tw-min-h-[48px]">
            <q-item-section avatar class="tw-min-w-0 tw-pr-3">
              <q-avatar size="40px" color="primary">
                <img :src="LoginUtils.getUserImageSrc(p.participant?.id ?? '', false)" alt="">
              </q-avatar>
            </q-item-section>
            <q-item-section>
              <q-item-label class="tw-font-medium tw-text-namaFontColor-light dark:tw-text-namaFontColor-dark">
              <span v-text="p.participant?.id === presenter.currentUserInfo?.id
                      ? Translator.translateId('You')
                      : Translator.translateRef(p.participant)"/>
              </q-item-label>
              <q-item-label caption>
                <div class="tw-flex tw-items-center tw-gap-1">
                  <NamaIcon defaultIcon="circle" class="!tw-text-[6px]"
                            :class="[presenter.isUserOnline(p.participant?.id ?? '') ? 'active-green' : 'active-gray']"/>
                  <NamaLabel :text="presenter.isUserOnline(p.participant?.id ?? '') ? 'activeNow' : 'notActive'"
                             class="!tw-text-xs"/>
                </div>
              </q-item-label>
            </q-item-section>
            <q-item-section v-if="isParticipantAdmin(p.participant?.id)" side>
            <span class="tw-text-[10px] tw-font-medium tw-text-primary-light dark:tw-text-primary-dark
                         tw-bg-primary-light/10 dark:tw-bg-primary-dark/10 tw-px-2 tw-py-0.5 tw-rounded-full">
              {{
                meta?.groupOwner?.id === p.participant?.id
                  ? Translator.translateId('chatGroupOwner')
                  : Translator.translateId('chatGroupAdmin')
              }}
            </span>
            </q-item-section>
            <q-item-section v-if="isAdmin && p.participant?.id !== presenter.currentUserInfo?.id
                                && p.participant?.id !== meta?.groupOwner?.id" side>
              <q-btn round flat dense size="sm" class="tw-text-gray-400">
                <NamaIcon defaultIcon="more_vert" class="tw-size-4"/>
                <q-menu anchor="bottom end" self="top end">
                  <q-list dense class="tw-min-w-[160px]">
                    <q-item v-if="isOwner && p.role !== 'Admin'" clickable v-close-popup @click="makeAdmin(p.participant ?? {})">
                      <q-item-section avatar class="tw-min-w-0 tw-pr-2">
                        <NamaIcon defaultIcon="admin_panel_settings" class="tw-size-4 tw-text-primary-light dark:tw-text-primary-dark"/>
                      </q-item-section>
                      <q-item-section>
                        <q-item-label class="tw-text-sm" v-text="Translator.translateId('chatMakeAdmin')"/>
                      </q-item-section>
                    </q-item>
                    <q-item v-if="isOwner && p.role === 'Admin'" clickable v-close-popup @click="removeAdmin(p.participant ?? {})">
                      <q-item-section avatar class="tw-min-w-0 tw-pr-2">
                        <NamaIcon defaultIcon="remove_moderator" class="!tw-text-xl"/>
                      </q-item-section>
                      <q-item-section>
                        <q-item-label class="tw-text-sm tw-font-semibold" v-text="Translator.translateId('chatRemoveAdmin')"/>
                      </q-item-section>
                    </q-item>
                    <q-item clickable v-close-popup @click="removeMember(p.participant ?? {})">
                      <q-item-section avatar class="tw-min-w-0 tw-pr-2">
                        <NamaIcon defaultIcon="person_remove" class="!tw-text-xl"/>
                      </q-item-section>
                      <q-item-section>
                        <q-item-label class="tw-text-sm tw-font-semibold" v-text="Translator.translateId('chatRemoveUser')"/>
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-menu>
              </q-btn>
            </q-item-section>
          </q-item>
        </q-list>
      </div>
    </q-card-section>
    <q-dialog v-model="showAddMember">
      <ChatAddMemberDialog :presenter="presenter" :participants="participants"
                           @close="showAddMember = false" @added="onMemberAdded"/>
    </q-dialog>
    <q-dialog v-model="showViewMembers">
      <ChatViewMembersDialog :presenter="presenter" :participants="participants"
                             @close="showViewMembers = false"/>
    </q-dialog>
  </q-drawer>
</template>
